import { apiFetch } from '@/lib/api';
import type { BlogPost, BlogCategory, PostNavigation, PaginatedResponse } from '@/types/blog';

export const blogService = {
  // ─── Admin Post CRUD ─────────────────────────────────────────────

  async listPosts(filters?: {
    status?: string;
    category_id?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ data: BlogPost[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.category_id) params.set('category_id', filters.category_id);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.pageSize) params.set('pageSize', String(filters.pageSize));
    const res = await apiFetch(`/api/admin/blog/posts?${params}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error?.message || json.error || 'Failed to list posts');
    return json;
  },

  async getPost(id: string): Promise<BlogPost> {
    const res = await apiFetch(`/api/admin/blog/posts/${id}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error?.message || json.error || 'Failed to get post');
    return json.data;
  },

  async createPost(data: {
    title: string;
    slug: string;
    content: string;
    excerpt?: string;
    featured_image?: string;
    og_image?: string;
    meta_title?: string;
    meta_description?: string;
    author_name?: string;
    category_id?: string;
    status?: string;
  }): Promise<BlogPost> {
    const res = await apiFetch('/api/admin/blog/posts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error?.message || json.error || 'Failed to create post');
    return json.data;
  },

  async updatePost(id: string, data: Record<string, unknown>): Promise<BlogPost> {
    const res = await apiFetch(`/api/admin/blog/posts/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error?.message || json.error || 'Failed to update post');
    return json.data;
  },

  async deletePost(id: string): Promise<void> {
    const res = await apiFetch(`/api/admin/blog/posts/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const json = await res.json();
      throw new Error(json.error?.message || json.error || 'Failed to delete post');
    }
  },

  async publishPost(id: string): Promise<BlogPost> {
    const res = await apiFetch(`/api/admin/blog/posts/${id}/publish`, { method: 'POST' });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error?.message || json.error || 'Failed to publish post');
    return json.data;
  },

  async unpublishPost(id: string): Promise<BlogPost> {
    const res = await apiFetch(`/api/admin/blog/posts/${id}/unpublish`, { method: 'POST' });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error?.message || json.error || 'Failed to unpublish post');
    return json.data;
  },

  async checkSlug(slug: string, excludeId?: string): Promise<{ available: boolean; suggested?: string }> {
    const params = new URLSearchParams({ slug });
    if (excludeId) params.set('exclude_id', excludeId);
    const res = await apiFetch(`/api/admin/blog/slug-check?${params}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error?.message || json.error || 'Failed to check slug');
    return json.data;
  },

  // ─── Admin Category CRUD ─────────────────────────────────────────

  async listCategories(): Promise<BlogCategory[]> {
    const res = await apiFetch('/api/admin/blog/categories');
    const json = await res.json();
    if (!res.ok) throw new Error(json.error?.message || json.error || 'Failed to list categories');
    return json.data;
  },

  async createCategory(data: {
    name: string;
    slug: string;
    description?: string;
    meta_title?: string;
    meta_description?: string;
    sort_order?: number;
  }): Promise<BlogCategory> {
    const res = await apiFetch('/api/admin/blog/categories', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error?.message || json.error || 'Failed to create category');
    return json.data;
  },

  async updateCategory(id: string, data: Record<string, unknown>): Promise<BlogCategory> {
    const res = await apiFetch(`/api/admin/blog/categories/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error?.message || json.error || 'Failed to update category');
    return json.data;
  },

  async deleteCategory(id: string): Promise<void> {
    const res = await apiFetch(`/api/admin/blog/categories/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const json = await res.json();
      throw new Error(json.error?.message || json.error || 'Failed to delete category');
    }
  },

  // ─── Public ──────────────────────────────────────────────────────

  async listPublicPosts(params?: {
    category?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PaginatedResponse<BlogPost>> {
    const searchParams = new URLSearchParams();
    if (params?.category) searchParams.set('category', params.category);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
    const res = await fetch(`/api/blog/posts?${searchParams}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error?.message || json.error || 'Failed to list posts');
    return json.data;
  },

  async getPublicPost(slug: string): Promise<BlogPost> {
    const res = await fetch(`/api/blog/posts/${encodeURIComponent(slug)}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error?.message || json.error || 'Failed to get post');
    return json.data;
  },

  async listPublicCategories(): Promise<BlogCategory[]> {
    const res = await fetch('/api/blog/categories');
    const json = await res.json();
    if (!res.ok) throw new Error(json.error?.message || json.error || 'Failed to list categories');
    return json.data;
  },

  async getRecentPosts(limit = 5): Promise<BlogPost[]> {
    const res = await fetch(`/api/blog/recent?limit=${limit}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error?.message || json.error || 'Failed to get recent posts');
    return json.data;
  },

  async getPostNavigation(slug: string): Promise<PostNavigation> {
    const res = await fetch(`/api/blog/posts/${encodeURIComponent(slug)}/navigation`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error?.message || json.error || 'Failed to get navigation');
    return json.data;
  },
};

export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}
